/**
 *  FAQ 목록
 *
 *  @author  Bruce.Cheong (2026-05-06)
 *
 */

/** External import */
import { useEffect, useState }              from    "react";

/** Internal import */
import CSS                                  from    "./FaqList.module.css";

import fetchBE                              from    "@/Common/fetch/fetchBE";
import CardContainer                        from    "@/Component/Elements/CardContainer/CardContainer";
import Button                               from    "@/Component/Elements/Button/Normal/Button";
import SearchIcon                           from    "@/Component/Elements/Icon/SearchIcon/SearchIcon";
import Input                                from    "@/Component/Elements/Input/Input";
import MainTitle                            from    "@/Component/Elements/MainTitle/MainTitle";

// import { ReactComponent as IconContract}    from    "./MyPage/img/contract.svg";
// import { ReactComponent as IconQnA}         from    "./MyPage/img/qna.svg";
// import { ReactComponent as IconFAQ}         from    "./MyPage/img/faq.svg";

function FaqList() {
    const [fetchFaqList, setFetchFaqList] = useState<it_FaqItem[]>([]);
    const [isFaqListLoaded, setIsFaqListLoaded] = useState<boolean>(false);
    const [searchKeyword, setSearchKeyword] = useState<string>("");
    const [fetchErrorMessage, setFetchErrorMessage] = useState<string>("");

    useEffect(() => {
        f_Search();

        return () => {
            setFetchFaqList([]);
            setIsFaqListLoaded(false);
            setFetchErrorMessage("");
        }
    }, []);

    async function f_Search() {
        setIsFaqListLoaded(false);
        setFetchErrorMessage("");

        const _fetchParam = {
            board_pk: 2,
            group_id: 8,
            search_value: searchKeyword.trim(),
        };

        const _fetchData = await fetchBE.get.postList(_fetchParam);

        if (!_fetchData.isSuccess) {
            setFetchFaqList([]);
            setFetchErrorMessage("FAQ 목록을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.");
            setIsFaqListLoaded(true);
            return;
        }

        let postList: Record<string, unknown>[] = [];
        const fetchDataRoot = _fetchData.data as null | Record<string, unknown> | Record<string, unknown>[];

        if (fetchDataRoot && !Array.isArray(fetchDataRoot) && Array.isArray(fetchDataRoot.data)) {
            postList = fetchDataRoot.data as Record<string, unknown>[];
        } else if (Array.isArray(fetchDataRoot)) {
            postList = fetchDataRoot;
        }

        setFetchFaqList(
            postList
                .map((iRow, index) => {
                    const _postPk = Number(iRow.post_pk);
                    const _category = iRow.category ? `${iRow.category}`.trim() : "";
                    const _title = iRow.post_title ? `${iRow.post_title}`.trim() : "";
                    const _content = iRow.post_contents ? `${iRow.post_contents}`.trim() : "";

                    return {
                        ...iRow,
                        post_pk: Number.isNaN(_postPk) ? index : _postPk,
                        category: _category,
                        title: _title,
                        content: _content,
                    };
                })
                .filter((iRow) => iRow.title || iRow.content)
        );
        setIsFaqListLoaded(true);
    }

    function hdlChangeSearchInput(iEvt: React.ChangeEvent<HTMLInputElement>) {
        setSearchKeyword(iEvt.target.value);
    }

    function hdlKeyDownSearchInput(iEvt: React.KeyboardEvent<HTMLInputElement>) {
        if (iEvt.key == "Enter") { f_Search(); }
    }

    function hdlClickSearch() {
        f_Search();
    }

    return (
        <div className={CSS.FaqList}>
            <MainTitle title="FAQ" />

            <CardContainer>
                <div className={CSS.HeaderCard}>
                    <h3 className={CSS.HeaderCardTitle}>
                        자주 묻는 질문
                    </h3>
                    <p className={CSS.HeaderCardDescription}>
                        고객님들이 많이 물어보는 질문을 모아두었습니다.
                    </p>
                </div>
            </CardContainer>

            <div className={CSS.SearchToolbar}>
                <label className={CSS.SearchField}>
                    <span className={CSS.SearchFieldIcon}>
                        <SearchIcon />
                    </span>
                    <Input
                        value={searchKeyword}
                        inputClassName={CSS.SearchFieldInput}
                        type="text"
                        placeholder="검색어를 입력하세요"
                        aria-label="검색어를 입력하세요"
                        onChange={hdlChangeSearchInput}
                        onKeyDown={hdlKeyDownSearchInput}
                    />
                    <Button
                        preset={"background_white"}
                        class_name_list={[CSS.SearchFieldButton]}
                        onClick={hdlClickSearch}
                    >
                        검색
                    </Button>
                </label>
            </div>

            {fetchErrorMessage && (
                <CardContainer class_name_list={[CSS.FeedbackCard, CSS.FeedbackCardError]}>
                    <div className={CSS.FeedbackTitle}>
                        FAQ 목록을 불러오지 못했습니다.
                    </div>
                    <p className={CSS.FeedbackDescription}>
                        {fetchErrorMessage}
                    </p>
                </CardContainer>
            )}

            {!fetchErrorMessage && !isFaqListLoaded && (
                <CardContainer class_name_list={[CSS.FeedbackCard]}>
                    <div className={CSS.FeedbackTitle}>
                        FAQ 목록을 불러오는 중입니다.
                    </div>
                    <p className={CSS.FeedbackDescription}>
                        잠시만 기다려 주세요.
                    </p>
                </CardContainer>
            )}

            {!fetchErrorMessage && isFaqListLoaded && fetchFaqList.length === 0 && (
                <CardContainer class_name_list={[CSS.FeedbackCard]}>
                    <div className={CSS.FeedbackTitle}>
                        {searchKeyword.trim()
                            ? "검색 결과가 없습니다."
                            : "등록된 FAQ가 없습니다."}
                    </div>
                    <p className={CSS.FeedbackDescription}>
                        {searchKeyword.trim()
                            ? "다른 검색어로 다시 시도해 주세요."
                            : "FAQ가 등록되면 이곳에 표시됩니다."}
                    </p>
                </CardContainer>
            )}

            {(!fetchErrorMessage && isFaqListLoaded && fetchFaqList.length > 0) && (
                <List
                    listData={fetchFaqList}
                />
            )}
        </div>
    );
}

export default FaqList;

interface it_FaqItem {
    post_pk: number;
    category: string;
    title: string;
    content: string;
}

interface it_ListProps {
    listData ?: it_FaqItem[];
}


function List(iProps: it_ListProps) {
    const [expandedIndex, setExpandedIndex] = useState<number>(0);

    return (
        <div className={CSS.FaqCardList} aria-label="FAQ 목록">
            {iProps.listData?.map((item, index) => {
                const isExpanded = expandedIndex === index;
                const answerId = `faq-answer-${index}`;

                return (
                    <CardContainer
                        key={item.post_pk}
                        noPadding
                        class_name_list={[
                            CSS.FaqCard,
                            ...(isExpanded ? [CSS.FaqCardExpanded] : [])
                        ]}
                    >
                        <button
                            type="button"
                            className={CSS.FaqCardButton}
                            onClick={() => {
                                setExpandedIndex((prev) => {
                                    if (prev === index) {
                                        return -1;
                                    }

                                    return index;
                                });
                            }}
                            aria-expanded={isExpanded}
                            aria-controls={answerId}
                        >
                            {item.category && (
                                <span className={CSS.FaqCardBadge}>
                                    {item.category}
                                </span>
                            )}
                            <span className={CSS.FaqCardQuestion}>
                                {item.title}
                            </span>
                            <span className={CSS.FaqCardChevron} aria-hidden="true" />
                        </button>

                        {isExpanded && (
                            <div id={answerId} className={CSS.FaqCardAnswer}>
                                <span className={CSS.FaqCardAnswerLabel}>A.</span>
                                <p className={CSS.FaqCardAnswerText}>
                                    {item.content}
                                </p>
                            </div>
                        )}
                    </CardContainer>
                )
            })}
        </div>
    );

}
